import { useEffect, useState, useCallback, useRef, Fragment } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { GoCardlessCheckoutDialog } from "@/components/GoCardlessCheckoutDialog";
import { SepaCheckoutDialog } from "@/components/SepaCheckoutDialog";
import { SepaStatusBadge } from "@/components/SepaStatusBadge";
import { ProvinciaField, AddressAutocompleteField, EditBuildingButton } from "@/components/condomini/BuildingForm";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import {
  Building2, Loader2, ShoppingCart, Landmark,
  Crown, TrendingUp, Gauge, Coins,
  Plus, Pencil, Trash2, UploadCloud, CircleDollarSign, Check, Shield, Gem, Sparkles,
  Fingerprint, Copy, Search, MessageSquare, AlertTriangle, ArrowRight, Gift, CheckCircle2,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

/* ------------------- Riepilogo pacchetti SMS ------------------- */
const SmsPackageCard = () => {
  const [wallet, setWallet] = useState(null);
  const nav = useNavigate();
  useEffect(() => { api.get("/sms/wallet").then((r) => setWallet(r.data)).catch(() => setWallet(null)); }, []);
  if (!wallet) return null;
  const bal = wallet.pool_balance ?? 0;
  const low = bal < 10;
  return (
    <section data-testid="sms-package-summary">
      <div className="flex items-center gap-2 mb-4">
        <div className="text-xs uppercase tracking-[0.18em] text-[#22C55E] font-semibold flex items-center gap-2">
          <MessageSquare className="h-4 w-4" />Pacchetti SMS
        </div>
        <div className="h-px flex-1 bg-border" />
      </div>
      <button
        data-testid="sms-package-card"
        onClick={() => nav("/sedio-partner")}
        className={`w-full text-left rounded-2xl border p-6 flex flex-wrap items-center justify-between gap-4 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg cursor-pointer ${low ? "border-amber-500/60 bg-gradient-to-br from-amber-500/15 to-transparent" : "border-border bg-gradient-to-br from-[#22C55E14] to-transparent hover:border-foreground/20"}`}>
        <div className="flex items-center gap-4">
          <div className={`h-14 w-14 rounded-2xl flex items-center justify-center shrink-0 ${low ? "bg-amber-500/20" : "bg-[#22C55E]/15"}`}>
            {low ? <AlertTriangle className="h-7 w-7 text-amber-500" /> : <MessageSquare className="h-7 w-7 text-[#22C55E]" />}
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.18em] font-semibold text-muted-foreground">Saldo pacchetti SMS residuo</div>
            <div data-testid="sms-package-balance" className={`font-head text-4xl font-extrabold tracking-tighter mt-1 ${low ? "text-amber-500" : ""}`}>{bal}</div>
            <div className={`text-sm mt-1 ${low ? "text-amber-600 font-medium" : "text-muted-foreground"}`}>
              {low ? "Saldo pacchetti in esaurimento — ricarica ora" : "SMS acquistati ancora disponibili sull'account"}
            </div>
          </div>
        </div>
        <div
          data-testid="sms-package-recharge"
          className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${low ? "bg-amber-500 text-white hover:bg-amber-600" : "bg-[#22C55E] text-white hover:bg-[#1DA84E]"}`}>
          Ricarica SMS <ArrowRight className="h-4 w-4" />
        </div>
      </button>
    </section>
  );
};

const eur = (n) => `€ ${(Number(n) || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Stile per piano: icona + colore accento + claim. Premium è evidenziato ("Consigliato").
const PLAN_STYLE = {
  essenziale: { icon: Shield, accent: "#007BFF", tagline: "Per iniziare" },
  premium: { icon: Gem, accent: "#007BFF", featured: true, tagline: "La scelta della maggior parte degli studi", highlight: "Sblocca la Sincronizzazione catastale" },
  elite: { icon: Crown, accent: "#C99700", tagline: "Tutto incluso, massima resa" },
};

const EMPTY_PENDING = { name: "", codice_fiscale: "", address: "", citta: "", provincia: "", cap: "", iban: "", sdi: "" };

/* ------------------- Condizioni di abbonamento ------------------- */
const SUBSCRIPTION_TERMS = `CONDIZIONI GENERALI DI ABBONAMENTO — SEDIO

Fornitore: [Ragione sociale] — P.IVA [__________] — sede legale in [indirizzo completo] — PEC [__________] (di seguito "il Fornitore" o "Sedio").
Cliente: l'amministratore di condominio che attiva l'abbonamento (di seguito "il Cliente"), che opera nell'ambito della propria attività professionale.

Le presenti Condizioni Generali disciplinano l'attivazione e la gestione degli abbonamenti annuali ai servizi della piattaforma Sedio (di seguito "il Servizio") per ciascun condominio gestito dal Cliente.

1. OGGETTO
L'abbonamento dà diritto all'utilizzo delle funzionalità della piattaforma Sedio per un (1) condominio, per la durata di dodici (12) mesi a decorrere dalla data di attivazione. È richiesto un abbonamento distinto per ciascun condominio.

2. CORRISPETTIVO E IVA
Il canone annuale è indicato nella pagina di acquisto, nel piano prescelto (Essenziale, Premium o Elite). Gli importi si intendono comprensivi dell'IVA nella misura di legge (attualmente 22%). Il dettaglio dell'imposta è riportato in fase di pagamento e nella documentazione fiscale.

3. PAGAMENTO E ADDEBITO SEPA
Il pagamento avviene tramite addebito diretto SEPA sull'IBAN indicato dal Cliente per il condominio, gestito in modo sicuro dal fornitore dei servizi di pagamento (Stripe). Autorizzando l'addebito, il Cliente conferma di essere titolare o legittimato all'uso del conto indicato.

4. RINNOVO AUTOMATICO
Salvo disdetta, l'abbonamento si rinnova automaticamente di anno in anno, con addebito del canone allora vigente. Il Cliente può disdire il rinnovo in qualsiasi momento dalla sezione "Acquisti e abbonamento": le funzioni restano attive fino alla scadenza del periodo già pagato.

5. PROGRAMMA SEDIO PARTNER
Gli eventuali corrispettivi "SedioCoin" riconosciuti all'Amministratore per l'attività di onboarding sono disciplinati dal separato contratto Sedio Partner e non costituiscono uno sconto sul canone né un cashback legato al prezzo.

6. ATTIVAZIONE DEL CONDOMINIO
Il condominio comparirà nelle aree operative (Anagrafiche, Pratiche, ricerca) solo dopo l'attivazione dell'abbonamento. Alla cessazione dell'abbonamento il condominio viene disattivato ma i dati restano conservati e ripristinabili con la riattivazione.

7. TRATTAMENTO DEI DATI
I dati del Cliente e del condominio sono trattati nel rispetto del Reg. UE 2016/679 (GDPR) esclusivamente per l'erogazione del Servizio e degli adempimenti connessi.

8. LIMITAZIONI DI RESPONSABILITÀ
Il Servizio è fornito "così com'è". Il Fornitore adotta misure ragionevoli per garantirne la continuità ma non risponde di interruzioni dovute a cause esterne o a servizi di terze parti.

9. SOSPENSIONE E RISOLUZIONE
In caso di mancato buon fine dell'addebito SEPA, di uso illecito o abusivo del Servizio o di violazione delle presenti condizioni, il Fornitore può sospendere il Servizio previa comunicazione e, in difetto di regolarizzazione entro 15 (quindici) giorni, risolvere l'abbonamento ai sensi dell'art. 1456 c.c. In caso di risoluzione il condominio viene disattivato; i dati restano conservati e ripristinabili secondo la clausola 6.

10. MODIFICHE
Il Fornitore potrà aggiornare le presenti condizioni dandone comunicazione con congruo preavviso. L'uso continuato del Servizio dopo l'aggiornamento ne costituisce accettazione; in mancanza, il Cliente può disdire il rinnovo ai sensi della clausola 4.

11. LEGGE APPLICABILE E FORO COMPETENTE
Il presente rapporto è regolato dalla legge italiana. Trattandosi di rapporto tra professionisti, per ogni controversia è competente in via esclusiva il Foro di [città sede del Fornitore].

12. ACCETTAZIONE
La spunta di accettazione costituisce manifestazione di volontà del Cliente di aderire integralmente alle presenti condizioni prima del primo pagamento di un abbonamento. Ai sensi degli artt. 1341-1342 c.c., il Cliente approva specificamente le clausole 4 (rinnovo automatico e disdetta), 8 (limitazioni di responsabilità), 9 (sospensione e risoluzione) e 11 (foro competente).

Scorrendo fino in fondo e cliccando "Accetto le condizioni" il Cliente dichiara di aver letto, compreso e accettato le clausole sopra riportate.`;

const SubscriptionTermsDialog = ({ open, onClose, onAccept, busy }) => {
  const [atEnd, setAtEnd] = useState(false);
  useEffect(() => { if (open) setAtEnd(false); }, [open]);
  const onScroll = (e) => {
    const el = e.target;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 24) setAtEnd(true);
  };
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="subscription-terms-dialog">
        <DialogHeader>
          <DialogTitle>Condizioni di abbonamento</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground -mt-1">
          Per attivare il tuo primo collegamento devi accettare le condizioni. Leggile fino in fondo per proseguire.
        </p>
        <div
          data-testid="subscription-terms-scroll"
          onScroll={onScroll}
          className="h-72 overflow-y-auto scroll-thin rounded-lg border border-border bg-secondary/40 p-4 text-[13px] leading-relaxed whitespace-pre-wrap font-mono"
        >
          {SUBSCRIPTION_TERMS}
        </div>
        {!atEnd && (
          <div className="text-[11px] text-amber-600" data-testid="subscription-terms-scroll-hint">
            ↓ Scorri le condizioni fino in fondo per abilitare l'accettazione.
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} className="rounded-md">Annulla</Button>
          <Button
            data-testid="subscription-terms-accept-button"
            disabled={!atEnd || busy}
            onClick={onAccept}
            className="bg-[#007BFF] hover:bg-[#0069D9] rounded-md"
          >
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}Accetto le condizioni
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* ---- Attivazione GRATUITA temporanea (nessun pagamento, finché non si sceglie il metodo) ---- */
const FreeActivateDialog = ({ pending, options, onClose, onActivated }) => {
  const open = !!pending;
  const [option, setOption] = useState("premium");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (pending) setOption("premium"); }, [pending]);

  const activate = async () => {
    setBusy(true);
    try {
      const payload = pending?._building_id
        ? { building_id: pending.id, option }
        : { pending_id: pending.id, option };
      await api.post("/billing/condominio/activate-free", payload);
      toast.success(`Condominio "${pending?.name}" attivato gratuitamente.`);
      onActivated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Attivazione non riuscita.");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !busy) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="free-activate-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Gift className="h-5 w-5 text-emerald-600" />Attiva condominio
          </DialogTitle>
        </DialogHeader>
        <div className="flex items-start gap-2 text-[12.5px] text-emerald-700 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900/50 rounded-md px-3 py-2.5" data-testid="free-activate-note">
          <Sparkles className="h-4 w-4 mt-0.5 shrink-0" />
          <span>Attivazione <b>gratuita</b> e immediata. Nessun pagamento richiesto per ora: il condominio
          «{pending?.name}» verrà abilitato subito.</span>
        </div>

        <div className="space-y-2 mt-2">
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Scegli il piano</div>
          {(options || []).map((o) => {
            const st = PLAN_STYLE[o.option] || PLAN_STYLE.essenziale;
            const sel = option === o.option;
            return (
              <button key={o.option} type="button" onClick={() => setOption(o.option)}
                data-testid={`free-activate-option-${o.option}`}
                className={`w-full text-left rounded-lg border p-3 flex items-center gap-3 transition-all
                  ${sel ? "border-[#007BFF] ring-2 ring-[#007BFF]/25 bg-[#007BFF]/[0.05]" : "border-border hover:border-[#007BFF]/40"}`}>
                <span className={`h-5 w-5 rounded-full border-2 flex items-center justify-center shrink-0 ${sel ? "border-[#007BFF] bg-[#007BFF]" : "border-muted-foreground/40"}`}>
                  {sel && <Check className="h-3 w-3 text-white" />}
                </span>
                <span className="flex-1 min-w-0">
                  <span className="font-semibold text-sm">{o.label}</span>
                  <span className="block text-[11px] text-muted-foreground">{eur(o.net_eur)}/anno + IVA · gratis ora</span>
                </span>
                {o.sedio_coin > 0 && (
                  <span className="inline-flex items-center gap-1 text-emerald-600 font-head font-bold text-xs">
                    <Coins className="h-3.5 w-3.5" />+{o.sedio_coin}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <DialogFooter className="mt-2">
          <Button variant="outline" onClick={onClose} disabled={busy} className="rounded-md">Annulla</Button>
          <Button data-testid="free-activate-confirm" onClick={activate} disabled={busy}
            className="bg-emerald-600 hover:bg-emerald-700 rounded-md">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
            Acquista
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* ------------------- Staging: inserisci & collega condomìni ------------------- */
const PurchaseSection = ({ options, onActivated, extraBuildings = [] }) => {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY_PENDING);
  const [editing, setEditing] = useState(null); // id being edited (inline) or null
  const [busy, setBusy] = useState(false);
  const [importing, setImporting] = useState(false);
  const [checkoutFor, setCheckoutFor] = useState(null); // pending item for SEPA dialog
  const [stripeCheckoutFor, setStripeCheckoutFor] = useState(null); // pending item for Stripe dialog
  const [stripeOption, setStripeOption] = useState("premium");
  const [termsAccepted, setTermsAccepted] = useState(null); // null=loading, true/false
  const [termsOpen, setTermsOpen] = useState(false);
  const [termsBusy, setTermsBusy] = useState(false);
  const [pendingAfterTerms, setPendingAfterTerms] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [confirmDeleteBuilding, setConfirmDeleteBuilding] = useState(null);
  const [deletingBuilding, setDeletingBuilding] = useState(false);
  const [pendingImportFile, setPendingImportFile] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [impDrag, setImpDrag] = useState(false);
  const fileRef = useRef(null);

  const onImpDrop = (e) => {
    e.preventDefault();
    setImpDrag(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) setPendingImportFile(f);
  };

  const load = useCallback(() => {
    setLoading(true);
    api.get("/pending-condomini")
      .then((r) => setPending(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/billing/terms").then((r) => setTermsAccepted(!!r.data.accepted)).catch(() => setTermsAccepted(false)); }, []);

  const acceptTerms = async () => {
    setTermsBusy(true);
    try {
      await api.post("/billing/terms/accept");
      setTermsAccepted(true);
      setTermsOpen(false);
      if (pendingAfterTerms) { setCheckoutFor(pendingAfterTerms); setPendingAfterTerms(null); }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impossibile registrare l'accettazione");
    } finally { setTermsBusy(false); }
  };

  const resetForm = () => { setForm(EMPTY_PENDING); setEditing(null); };

  const REQUIRED = ["name", "codice_fiscale", "address", "citta", "cap", "iban"];
  const isComplete = (p) => REQUIRED.every((k) => (p[k] || "").trim());

  const save = async () => {
    if (!isComplete(form)) { toast.error("Compila tutti i dati del condominio (compresi CAP e IBAN)."); return; }
    setBusy(true);
    try {
      if (editing) {
        await api.put(`/pending-condomini/${editing}`, form);
        toast.success("Condominio aggiornato");
      } else {
        await api.post("/pending-condomini", form);
        toast.success("Condominio aggiunto. Ora puoi collegarlo.");
      }
      resetForm();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore salvataggio");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/pending-condomini/${id}`); load(); if (editing === id) resetForm(); }
    catch { toast.error("Impossibile rimuovere"); }
    finally { setConfirmDeleteId(null); }
  };

  const removeBuilding = async () => {
    if (!confirmDeleteBuilding) return;
    setDeletingBuilding(true);
    try {
      await api.delete(`/buildings/${confirmDeleteBuilding.id}`);
      toast.success(`Condominio "${confirmDeleteBuilding.name}" eliminato`);
      setConfirmDeleteBuilding(null);
      onActivated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore durante l'eliminazione");
    } finally { setDeletingBuilding(false); }
  };

  const startEdit = (p) => {
    setEditing(p.id);
    setForm({ name: p.name || "", codice_fiscale: p.codice_fiscale || "", address: p.address || "",
      citta: p.citta || "", provincia: p.provincia || "", cap: p.cap || "", iban: p.iban || "", sdi: p.sdi || "" });
  };

  const startCheckout = (p) => {
    if (!isComplete(p)) {
      toast.error("Completa tutti i dati del condominio (CAP e IBAN inclusi) prima di collegarlo.");
      startEdit(p);
      return;
    }
    if (termsAccepted === false) {
      setPendingAfterTerms(p);
      setTermsOpen(true);
      return;
    }
    setCheckoutFor(p);
  };

  const doImport = async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/pending-condomini/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (r.data.duplicate) {
        toast.info(`"${r.data.name}" è già tra i condomìni da collegare: ho aggiornato i dati mancanti senza duplicarlo.`);
      } else {
        toast.success(`Condominio "${r.data.name}" importato dall'AI${r.data.iban ? " (IBAN rilevato)" : ""}. Controlla i dati e collegalo.`);
      }
      load();
      setImportOpen(false);
      setPendingImportFile(null);
      if (fileRef.current) fileRef.current.value = "";
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import non riuscito");
    } finally { setImporting(false); }
  };

  const _featMap = Object.fromEntries((options || []).map((o) => [o.option, o.features || []]));
  const extrasFor = (opt) => {
    const f = _featMap[opt] || [];
    if (opt === "premium") return f.filter((x) => !(_featMap.essenziale || []).includes(x));
    if (opt === "elite") return f.filter((x) => !(_featMap.premium || []).includes(x));
    return f;
  };

  return (
    <div className="bg-card border border-border rounded-xl p-6" data-testid="purchase-section">
      <div className="flex items-center gap-2 mb-1">
        <ShoppingCart className="h-4 w-4 text-[#007BFF]" />
        <h3 className="font-head text-lg font-semibold">Inserisci e collega i tuoi condomìni</h3>
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        Inserisci qui i condomìni (manualmente o importandoli dall'AI) e poi attiva il collegamento annuale con
        addebito diretto SEPA direttamente in Sedio. <strong>Il condominio comparirà nell'anagrafica solo dopo
        l'attivazione del collegamento.</strong>
      </p>

      {/* Info opzioni */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6 items-start">
        {options.map((o, i) => {
          const st = PLAN_STYLE[o.option] || PLAN_STYLE.essenziale;
          const Icon = st.icon;
          return (
            <div key={o.option} style={{ animationDelay: `${i * 80}ms` }}
              className={`group relative rounded-2xl border p-5 flex flex-col transition-all duration-300 animate-in fade-in-0 slide-in-from-bottom-3 fill-mode-both
                ${st.featured
                  ? "border-[#007BFF] ring-2 ring-[#007BFF]/30 shadow-xl shadow-[#007BFF]/15 bg-gradient-to-b from-[#007BFF]/[0.09] to-transparent sm:scale-[1.04] sm:z-10 hover:-translate-y-1.5"
                  : "border-border hover:border-[#007BFF]/40 hover:shadow-md hover:-translate-y-1"}`}
              data-testid={`option-info-${o.option}`}>
              {st.featured && (
                <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full bg-[#007BFF] text-white shadow-md">
                  <Sparkles className="h-3 w-3" />Consigliato
                </span>
              )}
              <div className="flex items-center gap-2.5 mb-3">
                <span className="h-9 w-9 rounded-xl flex items-center justify-center transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3"
                  style={{ background: `${st.accent}1A`, color: st.accent }}>
                  <Icon className="h-5 w-5" />
                </span>
                <div className="min-w-0">
                  <div className="font-head text-base font-bold leading-tight">{o.label}</div>
                  <div className="text-[10.5px] text-muted-foreground leading-tight">{st.tagline}</div>
                </div>
              </div>
              <div className="flex items-baseline gap-1.5">
                <span className="font-head text-3xl font-extrabold tracking-tight" style={{ color: st.accent }}>{eur(o.net_eur)}</span>
                <span className="text-[12px] text-muted-foreground font-medium">/anno + IVA</span>
              </div>
              <div className="text-[11px] text-muted-foreground mb-3">≈ {eur(o.net_eur / 12)}/mese · fatturazione annuale</div>
              {st.highlight && (
                <div className="mb-3 inline-flex items-center gap-1.5 rounded-lg bg-[#007BFF]/10 text-[#0056B3] text-[11px] font-semibold px-2.5 py-1.5 w-fit">
                  <Sparkles className="h-3.5 w-3.5 shrink-0" />{st.highlight}
                </div>
              )}
              {(o.features || []).length > 0 && (
                <ul className="space-y-1.5 pt-1" data-testid={`option-features-${o.option}`}>
                  {o.option !== "essenziale" && (
                    <li className="text-[11px] font-semibold flex items-center gap-1.5" style={{ color: st.accent }}>
                      <Check className="h-3.5 w-3.5 shrink-0" />Tutto di {o.option === "elite" ? "Premium" : "Essenziale"}
                    </li>
                  )}
                  {(o.option === "essenziale"
                    ? o.features
                    : extrasFor(o.option)
                  ).map((f) => (
                    <li key={f} className="text-[12px] text-foreground/80 flex items-center gap-1.5">
                      <Check className="h-3.5 w-3.5 shrink-0 text-emerald-600" />{f}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>

      {/* Corrispettivo partner in SedioCoin — riquadro SEPARATO, indipendente dal canone pagato */}
      <div className="relative overflow-hidden rounded-2xl border border-emerald-500/30 bg-gradient-to-br from-emerald-500/[0.09] via-emerald-500/[0.03] to-transparent p-5 mb-6 animate-in fade-in-0 fill-mode-both" data-testid="partner-coin-box">
        <div className="absolute -right-8 -top-8 h-32 w-32 rounded-full bg-emerald-500/10 blur-2xl pointer-events-none" />
        <div className="flex items-center gap-2.5 mb-1.5">
          <span className="h-9 w-9 rounded-xl bg-emerald-500/15 text-emerald-600 flex items-center justify-center">
            <Coins className="h-5 w-5 animate-pulse" />
          </span>
          <h4 className="font-head text-base font-bold flex flex-wrap items-center gap-2">
            Corrispettivo partner in SedioCoin
          </h4>
        </div>
        <p className="text-[12px] text-muted-foreground mb-4 max-w-3xl">
          I SedioCoin sono il <b>corrispettivo Sedio Partner</b> per l'attività di onboarding. Maturano al completamento dell'onboarding/configurazione del condominio. 1 SedioCoin = 1 € di corrispettivo partner.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {options.map((o, i) => (
            <div key={o.option} style={{ animationDelay: `${i * 80}ms` }}
              className={`rounded-xl border p-3.5 flex items-center justify-between transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md animate-in fade-in-0 slide-in-from-bottom-2 fill-mode-both
                ${o.sedio_coin > 0 ? "border-emerald-500/40 bg-emerald-500/[0.07] hover:border-emerald-500/60" : "border-border bg-card opacity-70"}`}
              data-testid={`partner-coin-row-${o.option}`}>
              <span className="text-sm font-medium">{o.label}</span>
              <span className={`inline-flex items-baseline gap-1 font-head font-extrabold ${o.sedio_coin > 0 ? "text-emerald-600" : "text-muted-foreground"}`}>
                <Coins className="h-4 w-4 self-center" />
                <span className="text-xl">{o.sedio_coin > 0 ? `+${o.sedio_coin}` : "0"}</span>
                <span className="text-[10px] font-semibold uppercase opacity-70">coin</span>
              </span>
            </div>
          ))}
        </div>
      </div>
      {/* Form inserimento */}
      <div className="rounded-lg border border-border p-4 mb-4">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="text-sm font-semibold">{editing ? "Modifica condominio" : "Aggiungi un condominio"}</div>
          <Button variant="outline" size="sm" className="rounded-md h-8" disabled={importing}
            onClick={() => setImportOpen(true)} data-testid="pending-import-btn">
            {importing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <UploadCloud className="h-4 w-4 mr-1.5" />}
            Importa dall'AI (documento)
          </Button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-1">
          <Input data-testid="pending-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Nome condominio *" className="h-9" />
          <Input data-testid="pending-cf" value={form.codice_fiscale} onChange={(e) => setForm((f) => ({ ...f, codice_fiscale: e.target.value }))} placeholder="Codice fiscale *" className="h-9" />
          <div data-testid="pending-address-wrap">
            <AddressAutocompleteField data={form} onChange={setForm} compact placeholder="Indirizzo *" />
          </div>
          <Input data-testid="pending-citta" value={form.citta} onChange={(e) => setForm((f) => ({ ...f, citta: e.target.value }))} placeholder="Città *" className="h-9" />
          <div data-testid="pending-provincia-wrap">
            <ProvinciaField data={form} onChange={setForm} compact />
          </div>
          <Input data-testid="pending-cap" value={form.cap} onChange={(e) => setForm((f) => ({ ...f, cap: e.target.value }))} placeholder="CAP *" className="h-9" />
          <Input data-testid="pending-iban" value={form.iban} onChange={(e) => setForm((f) => ({ ...f, iban: e.target.value }))} placeholder="IBAN del condominio *" className="h-9 font-mono uppercase" />
          <Input data-testid="pending-sdi" value={form.sdi} onChange={(e) => setForm((f) => ({ ...f, sdi: e.target.value }))} placeholder="Codice SDI (facoltativo)" className="h-9 font-mono uppercase" />
        </div>
        <p className="text-[11px] text-muted-foreground mb-3">I campi con * sono obbligatori per poter attivare il collegamento. Il codice SDI è facoltativo.</p>
        <div className="flex gap-2">
          <Button data-testid="pending-save-btn" onClick={save} disabled={busy} className="bg-[#007BFF] hover:bg-[#0069D9] rounded-md h-9">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
            {editing ? "Salva modifiche" : "Aggiungi condominio"}
          </Button>
          {editing && <Button variant="outline" onClick={resetForm} className="rounded-md h-9">Annulla</Button>}
        </div>
      </div>

      {/* Lista in attesa di collegamento */}
      <div className="text-sm font-semibold mb-2">Da collegare</div>
      {loading ? (
        <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
      ) : (pending.length === 0 && extraBuildings.length === 0) ? (
        <div className="text-sm text-muted-foreground bg-secondary/40 rounded-md px-3 py-4 text-center">
          Nessun condominio in attesa. Aggiungine uno qui sopra per collegarlo.
        </div>
      ) : (
        <div className="space-y-2" data-testid="pending-list">
          {pending.map((p) => (
            <div key={p.id} className="flex items-center gap-3 border border-border rounded-md px-3 py-2.5" data-testid={`pending-row-${p.id}`}>
              <Building2 className="h-4 w-4 text-amber-500 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{p.name}
                  <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-semibold">non attivo</span>
                </div>
                <div className="text-[11px] text-muted-foreground truncate">
                  {[p.address, p.citta].filter(Boolean).join(", ") || "—"}
                  {p.iban && <span className="ml-2 font-mono">· IBAN {p.iban}</span>}
                </div>
              </div>
              <button onClick={() => startEdit(p)} data-testid={`pending-edit-${p.id}`} className="p-1.5 rounded-md text-muted-foreground hover:text-[#007BFF] hover:bg-secondary"><Pencil className="h-4 w-4" /></button>
              <button onClick={() => setConfirmDeleteId(p.id)} data-testid={`pending-delete-${p.id}`} className="p-1.5 rounded-md text-muted-foreground hover:text-red-600 hover:bg-secondary"><Trash2 className="h-4 w-4" /></button>
              <Button size="sm" data-testid={`pending-subscribe-${p.id}`} onClick={() => startCheckout(p)} className="bg-[#007BFF] hover:bg-[#0069D9] rounded-md h-8">
                <CircleDollarSign className="h-4 w-4 mr-1.5" />Collega
              </Button>
            </div>
          ))}
          {/* Condomìni già presenti in anagrafica ma non ancora collegati */}
          {extraBuildings.map((b) => (
            <div key={b.id} className="flex items-center gap-3 border border-border rounded-md px-3 py-2.5" data-testid={`unsub-building-row-${b.id}`}>
              <Building2 className="h-4 w-4 text-amber-500 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{b.name}
                  <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-semibold">non collegato</span>
                </div>
                <div className="text-[11px] text-muted-foreground truncate">
                  {[b.address, b.citta].filter(Boolean).join(", ") || "—"}
                  {b.iban && <span className="ml-2 font-mono">· IBAN {b.iban}</span>}
                  <span className="ml-2">· da Gestione condomìni</span>
                </div>
              </div>
              <EditBuildingButton building={b} onSaved={() => onActivated?.()} />
              <button onClick={() => setConfirmDeleteBuilding(b)} data-testid={`unsub-building-delete-${b.id}`} className="p-1.5 rounded-md text-muted-foreground hover:text-red-600 hover:bg-secondary"><Trash2 className="h-4 w-4" /></button>
              <Button size="sm" data-testid={`unsub-building-subscribe-${b.id}`}
                onClick={() => {
                  if (!(b.name && b.address && b.citta && b.cap && b.codice_fiscale && b.iban)) {
                    toast.error("Completa i dati del condominio in Anagrafica (indirizzo, città, CAP, CF, IBAN) prima di collegarlo.");
                    return;
                  }
                  startCheckout({ ...b, _building_id: true });
                }}
                className="bg-[#007BFF] hover:bg-[#0069D9] rounded-md h-8">
                <CircleDollarSign className="h-4 w-4 mr-1.5" />Collega
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Attivazione gratuita temporanea (Stripe/GoCardless disattivati finché non si sceglie il metodo).
          Per riattivare i pagamenti, ripristinare i due dialog commentati qui sotto. */}
      <FreeActivateDialog
        pending={checkoutFor}
        options={options}
        onClose={() => setCheckoutFor(null)}
        onActivated={() => { setCheckoutFor(null); load(); onActivated?.(); }}
      />
      {/*
      <GoCardlessCheckoutDialog
        pending={checkoutFor}
        options={options}
        onClose={() => setCheckoutFor(null)}
        onUseStripe={(opt) => { setStripeOption(opt); setStripeCheckoutFor(checkoutFor); setCheckoutFor(null); }}
      />
      <SepaCheckoutDialog
        pending={stripeCheckoutFor}
        options={options}
        initialOption={stripeOption}
        onClose={() => setStripeCheckoutFor(null)}
        onActivated={() => { setStripeCheckoutFor(null); load(); onActivated?.(); }}
      />
      */}

      <SubscriptionTermsDialog
        open={termsOpen}
        onClose={() => { setTermsOpen(false); setPendingAfterTerms(null); }}
        onAccept={acceptTerms}
        busy={termsBusy}
      />

      <AlertDialog open={!!confirmDeleteId} onOpenChange={(v) => { if (!v) setConfirmDeleteId(null); }}>
        <AlertDialogContent data-testid="pending-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare questo condominio da collegare?</AlertDialogTitle>
            <AlertDialogDescription>
              Il condominio verrà rimosso dalla lista "Da collegare". L'operazione non è reversibile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="pending-delete-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="pending-delete-confirm-btn" onClick={() => remove(confirmDeleteId)} className="bg-red-600 hover:bg-red-700">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!confirmDeleteBuilding} onOpenChange={(v) => { if (!v) setConfirmDeleteBuilding(null); }}>
        <AlertDialogContent data-testid="unsub-building-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare questo condominio?</AlertDialogTitle>
            <AlertDialogDescription>
              Stai per eliminare <span className="font-semibold text-foreground">{confirmDeleteBuilding?.name}</span> e tutta la sua anagrafica condòmini da Gestione condomìni. L'operazione non è reversibile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="unsub-building-delete-cancel" disabled={deletingBuilding}>Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="unsub-building-delete-confirm-btn" disabled={deletingBuilding} onClick={(e) => { e.preventDefault(); removeBuilding(); }} className="bg-red-600 hover:bg-red-700">
              {deletingBuilding ? <Loader2 className="h-4 w-4 animate-spin" /> : "Elimina"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={importOpen} onOpenChange={(v) => { if (importing) return; setImportOpen(v); if (!v) { setPendingImportFile(null); setImpDrag(false); if (fileRef.current) fileRef.current.value = ""; } }}>
        <DialogContent className="max-w-lg" data-testid="pending-import-dialog">
          <DialogHeader>
            <DialogTitle>Importa un condominio con l'AI</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground -mt-1 mb-2">Carica o trascina un documento (Excel, PDF, immagine, CSV): l'AI estrae i dati del condominio e lo aggiunge alla lista "Da collegare".</p>
          <label
            onDragOver={(e) => { e.preventDefault(); setImpDrag(true); }}
            onDragEnter={(e) => { e.preventDefault(); setImpDrag(true); }}
            onDragLeave={(e) => { e.preventDefault(); setImpDrag(false); }}
            onDrop={onImpDrop}
            data-testid="pending-import-dropzone"
            className={`block border-2 border-dashed rounded-md p-6 text-center cursor-pointer transition-colors duration-200 ${impDrag ? "border-[#007BFF] bg-[#007BFF]/10" : "border-border hover:border-[#007BFF]"}`}>
            <input ref={fileRef} data-testid="pending-import-input" type="file" className="hidden" accept=".xlsx,.xls,.pdf,.png,.jpg,.jpeg,.webp,.txt,.csv"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) setPendingImportFile(f); }} />
            <UploadCloud className={`h-6 w-6 mx-auto mb-2 transition-colors ${impDrag ? "text-[#007BFF]" : "text-muted-foreground"}`} />
            <span className="text-sm text-muted-foreground">{pendingImportFile ? pendingImportFile.name : (impDrag ? "Rilascia qui il file" : "Trascina qui il file (Excel, PDF, CSV, immagine) o clicca")}</span>
          </label>
          <DialogFooter className="mt-3">
            <Button variant="outline" data-testid="pending-import-cancel" disabled={importing} onClick={() => { setImportOpen(false); setPendingImportFile(null); if (fileRef.current) fileRef.current.value = ""; }}>Annulla</Button>
            <Button data-testid="pending-import-confirm-btn" disabled={importing || !pendingImportFile} className="bg-[#007BFF] hover:bg-[#0069D9]"
              onClick={() => doImport(pendingImportFile)}>
              {importing ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Estrazione anagrafica…</> : "Importa"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/* ------------------- Condomìni attivi ------------------- */
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" }); } catch { return ""; } };

const PLAN_LABELS = { essenziale: "Essenziale", premium: "Premium", elite: "Elite", a: "Essenziale", b: "Premium" };
const normOpt = (o) => ({ a: "essenziale", b: "premium" }[o] || o);

const ActiveBuildingRow = ({ b, options, onChanged }) => {
  const [busy, setBusy] = useState("");
  const [confirm, setConfirm] = useState(null); // {type:'cancel'|'change', option, label}
  const subId = b.stripe_subscription_id;
  const canceled = b.renewal_status === "disdetto" || b.cancel_at_period_end;
  const curOpt = normOpt(b.subscription_option);

  const act = async (fn, okMsg) => {
    setBusy("1");
    try { await fn(); toast.success(okMsg); onChanged?.(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Operazione non riuscita"); }
    finally { setBusy(""); setConfirm(null); }
  };

  const runConfirm = () => {
    if (confirm?.type === "cancel") {
      act(() => api.post(`/billing/subscription/${subId}/cancel-renewal`), "Rinnovo automatico disdetto. Le funzioni restano attive fino a scadenza.");
    } else if (confirm?.type === "change") {
      act(() => api.post(`/billing/subscription/${subId}/change-plan`, { option: confirm.option }), `Al rinnovo passerai a ${confirm.label}.`);
    }
  };

  return (
    <div className="bg-secondary/50 rounded-md px-3 py-2.5" data-testid={`active-building-${b.id}`}>
      <div className="flex items-center gap-3">
        <Landmark className="h-4 w-4 text-[#007BFF] shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium truncate">
            {b.name}
            {b.subscription_status === "scaduto" && (
              <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 font-semibold" data-testid={`sub-expired-${b.id}`}>Abbonamento scaduto</span>
            )}
            {b.sepa_status ? <SepaStatusBadge status={b.sepa_status} className="ml-2" />
              : (b.paid_slot && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-semibold">a pagamento</span>)}
            {b.subscription_option && (() => {
              const st = PLAN_STYLE[curOpt] || PLAN_STYLE.essenziale;
              const PlanIcon = st.icon;
              return (
                <span
                  data-testid={`active-plan-badge-${b.id}`}
                  className="ml-2 inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wide border align-middle"
                  style={{ background: `${st.accent}1A`, color: st.accent, borderColor: `${st.accent}40` }}
                >
                  <PlanIcon className="h-3 w-3" />
                  {PLAN_LABELS[curOpt] || curOpt}
                </span>
              );
            })()}
          </div>
          {b.address && <div className="text-[11px] text-muted-foreground truncate">{b.address}{b.citta ? `, ${b.citta}` : ""}</div>}
          {b.subscription_status === "scaduto" && (
            <div className="text-[10px] text-red-600 mt-0.5" data-testid={`sub-expired-note-${b.id}`}>Il condominio è nascosto dalle aree operative (Pratiche, Anagrafiche, ricerca). I dati restano conservati: riattiva il rinnovo per ripristinarlo.</div>
          )}
          {b.sepa_status === "in_verifica" && (
            <div className="text-[10px] text-amber-600 mt-0.5">L'addebito SEPA è in corso di verifica dalla banca (fino a pochi giorni). Il condominio è già operativo.</div>
          )}
          {b.sepa_status === "addebito_fallito" && (
            <div className="text-[10px] text-red-600 mt-0.5">L'addebito è stato rifiutato dalla banca. Verifica l'IBAN e riprova il collegamento.</div>
          )}
        </div>
      </div>
      {subId && (
        <div className="mt-2 pt-2 border-t border-border/60 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[11px]">
          {canceled ? (
            <span className="text-amber-600 font-medium" data-testid={`renewal-status-${b.id}`}>
              Rinnovo disdetto{b.current_period_end ? ` · attivo fino al ${fmtDate(b.current_period_end)}` : ""}
            </span>
          ) : (
            <span className="text-muted-foreground" data-testid={`renewal-status-${b.id}`}>
              Rinnovo automatico annuale{b.current_period_end ? ` · prossimo il ${fmtDate(b.current_period_end)}` : ""}
            </span>
          )}
          <span className="flex-1" />
          {b.scheduled_option ? (
            <span className="text-[#007BFF] font-medium" data-testid={`scheduled-plan-${b.id}`}>
              Al rinnovo: {PLAN_LABELS[b.scheduled_option] || b.scheduled_option}
            </span>
          ) : (
            <select
              data-testid={`change-plan-${b.id}`} disabled={!!busy}
              value=""
              onChange={(e) => { const opt = e.target.value; if (opt) setConfirm({ type: "change", option: opt, label: PLAN_LABELS[opt] }); }}
              className="text-[11px] rounded-md border border-input bg-background px-2 py-1 text-[#007BFF]">
              <option value="">Cambia piano al rinnovo…</option>
              {["essenziale", "premium", "elite"].filter((o) => o !== curOpt).map((o) => (
                <option key={o} value={o}>Passa a {PLAN_LABELS[o]}</option>
              ))}
            </select>
          )}
          {canceled ? (
            <button data-testid={`resume-renewal-${b.id}`} disabled={!!busy}
              onClick={() => act(() => api.post(`/billing/subscription/${subId}/resume-renewal`), "Rinnovo automatico riattivato.")}
              className="text-emerald-600 hover:underline disabled:opacity-50">Riattiva rinnovo</button>
          ) : (
            <button data-testid={`cancel-renewal-${b.id}`} disabled={!!busy}
              onClick={() => setConfirm({ type: "cancel" })}
              className="text-red-600 hover:underline disabled:opacity-50">Disdici rinnovo</button>
          )}
        </div>
      )}

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent data-testid="renewal-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirm?.type === "cancel" ? "Disdire il rinnovo automatico?" : "Cambiare piano al rinnovo?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirm?.type === "cancel"
                ? `Il collegamento di "${b.name}" non si rinnoverà più automaticamente. Le funzioni resteranno attive fino alla scadenza${b.current_period_end ? ` (${fmtDate(b.current_period_end)})` : ""}, poi il condominio verrà disattivato. Potrai riattivare il rinnovo in qualsiasi momento prima della scadenza.`
                : `Dal prossimo rinnovo${b.current_period_end ? ` (${fmtDate(b.current_period_end)})` : ""}, "${b.name}" passerà a ${confirm?.label}. Nessun addebito immediato: il piano attuale resta valido fino a scadenza.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="renewal-confirm-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="renewal-confirm-ok" disabled={!!busy}
              onClick={(e) => { e.preventDefault(); runConfirm(); }}
              className={confirm?.type === "cancel" ? "bg-red-600 hover:bg-red-700" : "bg-[#007BFF] hover:bg-[#0069D9]"}>
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              {confirm?.type === "cancel" ? "Sì, disdici" : `Sì, passa a ${confirm?.label}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const ActiveBuildings = ({ buildings, options, onChanged }) => (
  <div className="bg-card border border-border rounded-xl p-6" data-testid="active-buildings">
    <div className="flex items-center gap-2 mb-4">
      <Building2 className="h-4 w-4 text-[#007BFF]" />
      <h3 className="font-head text-lg font-semibold">I tuoi condomìni</h3>
      <span className="ml-auto text-xs text-muted-foreground">{buildings.length} attivi</span>
    </div>
    {buildings.length === 0 ? (
      <div className="text-sm text-muted-foreground">Nessun condominio ancora acquistato.</div>
    ) : (
      <div className="space-y-2">
        {buildings.map((b) => <ActiveBuildingRow key={b.id} b={b} options={options} onChanged={onChanged} />)}
      </div>
    )}
  </div>
);

/* ------------------- Super Admin ------------------- */
const UsageByAccount = () => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/admin/usage-by-account?days=${days}`).then((r) => setData(r.data)).catch(() => {}); }, [days]);
  const fmt = (n) => new Intl.NumberFormat("it-IT").format(Math.round(n || 0));
  if (!data) return null;
  return (
    <div className="border-t border-border pt-4 mt-4" data-testid="admin-usage-by-account">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <Gauge className="h-4 w-4 text-[#4DA3FF]" />
        <h4 className="font-head text-sm font-semibold">Consumo AI per account</h4>
        <div className="ml-auto flex gap-1">
          {[7, 30, 90].map((d) => (
            <button key={d} onClick={() => setData(null) || setDays(d)} className={`text-[11px] px-2 py-0.5 rounded-full border ${days === d ? "bg-[#007BFF] text-white border-[#007BFF]" : "border-border text-muted-foreground"}`}>{d}g</button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div className="bg-secondary rounded-md p-3"><div className="text-lg font-extrabold">{fmt(data.grand_total.total_tokens)}</div><div className="text-[11px] uppercase tracking-wide text-muted-foreground">Token totali ({data.days}g)</div></div>
        <div className="bg-secondary rounded-md p-3"><div className="text-lg font-extrabold text-emerald-600 flex items-center gap-1"><Coins className="h-4 w-4" />{eur(data.grand_total.est_cost_eur)}</div><div className="text-[11px] uppercase tracking-wide text-muted-foreground">Costo stimato</div></div>
      </div>
    </div>
  );
};

const RevenueChart = () => {
  const [rev, setRev] = useState(null);
  useEffect(() => { api.get("/admin/revenue?months=12").then((r) => setRev(r.data)).catch(() => {}); }, []);
  if (!rev) return null;
  const hasData = (rev.series || []).some((s) => s.active_slots > 0);
  return (
    <div className="border-t border-border pt-4 mt-4" data-testid="admin-revenue-chart">
      <div className="flex items-center gap-2 mb-3">
        <TrendingUp className="h-4 w-4 text-emerald-600" />
        <h4 className="font-head text-sm font-semibold">Andamento ricavi</h4>
        <span className="ml-auto text-[12px] text-muted-foreground">Ultimi 12 mesi · {eur(rev.total_revenue_eur)}</span>
      </div>
      {!hasData ? (
        <div className="text-xs text-muted-foreground bg-secondary/40 rounded-md px-3 py-4 text-center">Nessun acquisto ancora registrato.</div>
      ) : (
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={rev.series} margin={{ top: 6, right: 10, left: -10, bottom: 0 }}>
              <defs><linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#007BFF" stopOpacity={0.35} /><stop offset="95%" stopColor="#007BFF" stopOpacity={0} /></linearGradient></defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(120,120,120,0.15)" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={48} tickFormatter={(v) => `€${v}`} />
              <Tooltip formatter={(v) => [eur(v), "Ricavo"]} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
              <Area type="monotone" dataKey="revenue_eur" stroke="#007BFF" strokeWidth={2} fill="url(#revGrad)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

const BuildingCodesPanel = () => {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    let alive = true;
    api.get("/admin/building-codes").then((r) => { if (alive) setRows(r.data?.buildings || []); }).catch(() => { if (alive) setRows([]); });
    return () => { alive = false; };
  }, []);

  const copy = (code) => { navigator.clipboard?.writeText(code); toast.success(`Codice ${code} copiato`); };

  const filtered = (rows || []).filter((b) => {
    if (!q.trim()) return true;
    const s = q.trim().toLowerCase();
    return [b.code, b.name, b.admin_email, b.admin_name, b.citta].some((v) => (v || "").toLowerCase().includes(s));
  });

  return (
    <div className="mt-6 border-t border-border pt-5" data-testid="building-codes-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div className="flex items-center gap-2">
          <Fingerprint className="h-4 w-4 text-[#007BFF]" />
          <h4 className="font-head text-base font-semibold">Codici univoci condomìni</h4>
          {rows && <span className="text-[11px] text-muted-foreground">({filtered.length})</span>}
        </div>
        <div className="relative">
          <Search className="h-3.5 w-3.5 text-muted-foreground absolute left-2.5 top-1/2 -translate-y-1/2" />
          <Input data-testid="building-codes-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Cerca codice, condominio o admin…" className="h-8 pl-8 text-sm w-64 max-w-full" />
        </div>
      </div>
      <p className="text-xs text-muted-foreground mb-3">Ogni condominio ha un codice univoco globale (SED-XXXXXX), valido su tutti gli amministratori, a prescindere dall'abbonamento.</p>
      {!rows ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-4"><Loader2 className="h-4 w-4 animate-spin" />Carico i codici…</div>
      ) : filtered.length === 0 ? (
        <div className="text-sm text-muted-foreground py-4">Nessun condominio trovato.</div>
      ) : (
        <div className="overflow-x-auto max-h-[300px] overflow-y-auto border border-border rounded-md" data-testid="building-codes-scroll">
          <table className="w-full text-sm" data-testid="building-codes-table">
            <thead className="sticky top-0 bg-card z-10"><tr className="text-left text-[11px] uppercase tracking-wide text-muted-foreground border-b border-border">
              <th className="py-2 px-3">Codice</th><th className="py-2 px-3">Condominio</th><th className="py-2 px-3">Località</th><th className="py-2 px-3">Amministratore</th>
            </tr></thead>
            <tbody>
              {filtered.map((b) => (
                <tr key={b.building_id} className="border-b border-border/50 hover:bg-secondary/40" data-testid={`building-code-row-${b.code}`}>
                  <td className="py-2 px-3">
                    <button type="button" onClick={() => copy(b.code)} title="Copia codice"
                      className="inline-flex items-center gap-1.5 font-mono font-semibold text-[#007BFF] hover:underline">
                      {b.code}<Copy className="h-3 w-3 opacity-60" />
                    </button>
                  </td>
                  <td className="py-2 px-3">
                    <div className="font-medium">{b.name || "—"}{b.is_demo && <span className="ml-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">demo</span>}</div>
                    {b.address && <div className="text-[11px] text-muted-foreground">{b.address}</div>}
                  </td>
                  <td className="py-2 px-3 text-muted-foreground">{[b.citta, b.provincia && `(${b.provincia})`].filter(Boolean).join(" ") || "—"}</td>
                  <td className="py-2 px-3"><div className="font-medium">{b.admin_name || "—"}</div><div className="text-[11px] text-muted-foreground">{b.admin_email}</div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};


const AdminPanel = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [delTarget, setDelTarget] = useState(null);
  const [delEmail, setDelEmail] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetConfirm, setResetConfirm] = useState("");
  const [resetting, setResetting] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/overview").then((r) => setData(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleSuspend = async (a) => {
    setBusy(a.user_id);
    try { await api.post(`/admin/users/${a.user_id}/suspend`, { suspended: !a.suspended }); toast.success(!a.suspended ? "Admin sospeso" : "Admin riattivato"); load(); }
    catch { toast.error("Operazione non riuscita"); }
    finally { setBusy(""); }
  };

  const toggleExempt = async (a) => {
    setBusy(a.user_id);
    try {
      await api.put(`/admin/administrators/${a.user_id}/exempt`, { exempt: !a.exempt });
      toast.success(!a.exempt ? "Esenzione concessa" : "Esenzione rimossa");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Operazione non riuscita"); }
    finally { setBusy(""); }
  };

  const openDelete = (a) => { setDelTarget(a); setDelEmail(""); };

  const doResetStats = async () => {
    setResetting(true);
    try {
      const r = await api.post("/admin/reset-payment-stats", { confirm: "RESET" });
      toast.success(`Statistiche azzerate: ${r.data.deleted} record di pagamento eliminati`);
      setResetOpen(false); setResetConfirm("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset non riuscito");
    } finally { setResetting(false); }
  };

  const confirmDelete = async () => {
    if (!delTarget) return;
    setDeleting(true);
    try {
      const r = await api.delete(`/admin/administrators/${delTarget.user_id}`);
      toast.success(`Amministratore ${r.data.deleted_email || delTarget.email} eliminato`);
      setDelTarget(null); setDelEmail("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eliminazione non riuscita");
    } finally { setDeleting(false); }
  };

  if (!data) return null;

  return (
    <div className="bg-card border border-amber-300/60 rounded-xl p-6" data-testid="super-admin-panel">
      <div className="flex items-center gap-2 mb-1"><Crown className="h-4 w-4 text-amber-500" /><h3 className="font-head text-lg font-semibold">Pannello Super Admin</h3></div>
      <p className="text-sm text-muted-foreground mb-4">Panoramica amministratori e ricavi. Le liquidazioni SedioCoin si gestiscono nella sezione "Sedio Partner".</p>
      <div className="grid grid-cols-3 gap-3 mb-2">
        <div className="bg-secondary rounded-md p-3"><div className="text-2xl font-extrabold">{data.total_admins}</div><div className="text-[11px] uppercase tracking-wide text-muted-foreground">Amministratori</div></div>
        <div className="bg-secondary rounded-md p-3"><div className="text-2xl font-extrabold">{data.total_paid_slots}</div><div className="text-[11px] uppercase tracking-wide text-muted-foreground">Condomìni pagati</div></div>
        <div className="bg-secondary rounded-md p-3"><div className="text-2xl font-extrabold text-emerald-600">{eur(data.revenue_estimate_eur)}</div><div className="text-[11px] uppercase tracking-wide text-muted-foreground">Ricavo stimato</div></div>
      </div>
      <div className="flex justify-end mb-4">
        <button onClick={() => { setResetConfirm(""); setResetOpen(true); }} data-testid="reset-payment-stats-btn"
          className="inline-flex items-center gap-1.5 text-[12px] font-medium text-red-600 hover:text-red-700 hover:underline">
          <Trash2 className="h-3.5 w-3.5" />Azzera statistiche pagamenti (dati di test)
        </button>
      </div>

      <RevenueChart />
      <UsageByAccount />

      <div className="overflow-x-auto mt-4">
        <table className="w-full text-sm" data-testid="admin-overview-table">
          <thead><tr className="text-left text-[11px] uppercase tracking-wide text-muted-foreground border-b border-border">
            <th className="py-2 pr-3">Amministratore</th><th className="py-2 pr-3">Ruolo</th><th className="py-2 pr-3">Condomìni</th><th className="py-2 pr-3">Stato</th><th className="py-2">Azioni</th>
          </tr></thead>
          <tbody>
            {data.admins.map((a) => (
              <tr key={a.user_id} className="border-b border-border/50" data-testid={`admin-row-${a.user_id}`}>
                <td className="py-2 pr-3"><div className="font-medium">{a.display_name || a.name || a.email}</div><div className="text-[11px] text-muted-foreground">{a.email}</div></td>
                <td className="py-2 pr-3">{a.role === "super_admin" ? <span className="inline-flex items-center gap-1 text-amber-600 font-semibold"><Crown className="h-3 w-3" />Super</span> : "Admin"}</td>
                <td className="py-2 pr-3">{a.paid_slots}/{a.buildings_count}</td>
                <td className="py-2 pr-3">{a.role === "super_admin" ? <span className="text-muted-foreground">—</span> : (
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {a.suspended ? <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-700">Sospeso</span> : <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Attivo</span>}
                    {a.exempt && <span data-testid={`admin-exempt-badge-${a.user_id}`} className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700" title={a.exempt_locked ? "Esente per configurazione di sistema" : "Esente da abbonamento"}>Esente</span>}
                  </div>
                )}</td>
                <td className="py-2">{a.role !== "super_admin" && (
                  <div className="flex items-center gap-3">
                    <button onClick={() => toggleExempt(a)} disabled={busy === a.user_id || a.exempt_locked} data-testid={`admin-exempt-${a.user_id}`}
                      title={a.exempt_locked ? "Esente per configurazione di sistema (non modificabile)" : (a.exempt ? "Rimuovi l'esenzione dall'abbonamento" : "Concedi l'esenzione dall'abbonamento")}
                      className={`text-[12px] font-medium hover:underline disabled:opacity-40 disabled:no-underline ${a.exempt ? "text-amber-600" : "text-muted-foreground"}`}>
                      {busy === a.user_id ? "…" : a.exempt ? "Rimuovi esenzione" : "Rendi esente"}
                    </button>
                    <button onClick={() => toggleSuspend(a)} disabled={busy === a.user_id} data-testid={`admin-suspend-${a.user_id}`} className={`text-[12px] font-medium hover:underline disabled:opacity-50 ${a.suspended ? "text-emerald-600" : "text-red-600"}`}>{busy === a.user_id ? "…" : a.suspended ? "Riattiva" : "Sospendi"}</button>
                    <button onClick={() => openDelete(a)} data-testid={`admin-delete-${a.user_id}`} title="Elimina amministratore" className="inline-flex items-center gap-1 text-[12px] font-medium text-red-600 hover:text-red-700 hover:underline">
                      <Trash2 className="h-3.5 w-3.5" />Elimina
                    </button>
                  </div>
                )}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <BuildingCodesPanel />

      <AlertDialog open={resetOpen} onOpenChange={(o) => { if (!o) { setResetOpen(false); setResetConfirm(""); } }}>
        <AlertDialogContent data-testid="reset-payment-stats-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600">Azzera statistiche pagamenti</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm">
                <p>Stai per eliminare <span className="font-semibold text-foreground">tutti i record di pagamento/abbonamento</span> (<span className="font-mono">condomino_payments</span>). "Condomìni pagati" e "Ricavo stimato" torneranno a zero per tutti.</p>
                <div className="rounded-md border border-red-200 bg-red-50 dark:bg-red-950/30 dark:border-red-900/50 p-3 text-red-700 dark:text-red-300">
                  <p className="font-semibold mb-1">Operazione IRREVERSIBILE.</p>
                  <ul className="list-disc list-inside mt-1 space-y-0.5">
                    <li>NON annulla gli abbonamenti reali su Stripe (solo i dati locali).</li>
                    <li>I condomìni non più coperti da un pagamento risulteranno "non pagati" (restano attivi solo quelli di admin esenti o in prova).</li>
                  </ul>
                </div>
                <div>
                  <p className="mb-1">Per confermare, digita <span className="font-mono font-semibold">RESET</span>:</p>
                  <Input value={resetConfirm} onChange={(e) => setResetConfirm(e.target.value)} placeholder="RESET" data-testid="reset-payment-stats-input" autoComplete="off" />
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="reset-payment-stats-cancel" disabled={resetting}>Annulla</AlertDialogCancel>
            <AlertDialogAction
              data-testid="reset-payment-stats-confirm"
              disabled={resetting || resetConfirm.trim().toUpperCase() !== "RESET"}
              onClick={(e) => { e.preventDefault(); doResetStats(); }}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {resetting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Azzera statistiche"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!delTarget} onOpenChange={(o) => { if (!o) { setDelTarget(null); setDelEmail(""); } }}>
        <AlertDialogContent data-testid="admin-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600">Elimina amministratore</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm">
                <p>Stai per eliminare <span className="font-semibold text-foreground">{delTarget?.display_name || delTarget?.name || delTarget?.email}</span> ({delTarget?.email}).</p>
                <div className="rounded-md border border-red-200 bg-red-50 dark:bg-red-950/30 dark:border-red-900/50 p-3 text-red-700 dark:text-red-300">
                  <p className="font-semibold mb-1">Operazione IRREVERSIBILE.</p>
                  <p>Verranno cancellati definitivamente tutti i dati di questo amministratore, tra cui:</p>
                  <ul className="list-disc list-inside mt-1">
                    <li><span className="font-semibold">{delTarget?.buildings_count ?? 0}</span> condomìni</li>
                    <li><span className="font-semibold">{delTarget?.pratiche_count ?? 0}</span> pratiche</li>
                    <li>documenti, portali e comunicazioni collegati</li>
                  </ul>
                </div>
                <div>
                  <p className="mb-1">Per confermare, digita l'email dell'amministratore:</p>
                  <Input value={delEmail} onChange={(e) => setDelEmail(e.target.value)} placeholder={delTarget?.email} data-testid="admin-delete-email-input" autoComplete="off" />
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-delete-cancel" disabled={deleting}>Annulla</AlertDialogCancel>
            <AlertDialogAction
              data-testid="admin-delete-confirm"
              disabled={deleting || delEmail.trim().toLowerCase() !== (delTarget?.email || "").toLowerCase()}
              onClick={(e) => { e.preventDefault(); confirmDelete(); }}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Elimina definitivamente"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

/* ------------------- Pagina ------------------- */
export default function AcquistiAbbonamenti() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [options, setOptions] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadBuildings = useCallback(() => api.get("/buildings?include_hidden=1").then((r) => setBuildings(r.data || [])).catch(() => {}), []);

  useEffect(() => {
    (async () => {
      try {
        const [o] = await Promise.all([api.get("/billing/options"), loadBuildings()]);
        setOptions(o.data.options || []);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    })();
  }, [loadBuildings]);

  return (
    <div className="fade-in" data-testid="acquisti-page">
      <header className="px-4 sm:px-10 pt-6 sm:pt-10 pb-6 border-b border-border bg-card/50">
        <div className="text-xs uppercase tracking-[0.2em] text-[#007BFF] font-semibold mb-2">Monetizzazione</div>
        <h1 className="font-head text-4xl font-extrabold tracking-tighter">Acquisti e collegamento</h1>
        <p className="text-muted-foreground mt-2 max-w-2xl">Attiva i tuoi condomìni scegliendo tra i piani Essenziale, Premium ed Elite. I corrispettivi SedioCoin si trovano nella sezione Sedio Partner.</p>
      </header>

      <div className="p-4 sm:p-10 space-y-6">
        {loading ? (
          <div className="bg-card border border-border rounded-xl p-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : (
          <Fragment>
            <PurchaseSection options={options} onActivated={loadBuildings}
              extraBuildings={(buildings || []).filter((b) => b.subscription_status !== "attivo")} />
            <ActiveBuildings buildings={(buildings || []).filter((b) => b.subscription_status === "attivo")} options={options} onChanged={loadBuildings} />
            {isSuper && <AdminPanel />}
            <SmsPackageCard />
          </Fragment>
        )}
      </div>
    </div>
  );
}
